import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { FaTimes } from 'react-icons/fa';
import teamService from '../../services/teamService';
import TagInput from './TagInput';
import { useI18n } from '../../i18n';
import '../Projects/ProjectComposer.css';

const DEFAULT_FORM = {
  title: '',
  content: '',
  visibility: 'personal',
  team_id: '',
  tags: [],
};

const NoteFormModal = ({ isOpen, onClose, onSubmit, initialData = null }) => {
  const { t } = useI18n();
  const [form, setForm] = useState(DEFAULT_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [teams, setTeams] = useState([]);
  const isTeam = form.visibility === 'team';

  useEffect(() => {
    if (!isOpen) return;
    teamService
      .list()
      .then((res) => {
        if (res.status) setTeams(res.data.teams || []);
      })
      .catch(() => { });
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    if (initialData) {
      setForm({
        title: initialData.title || '',
        content: initialData.content || '',
        visibility: initialData.team_id ? 'team' : 'personal',
        team_id: initialData.team_id || '',
        tags: Array.isArray(initialData.tags)
          ? initialData.tags.map((t) => t.name || t)
          : [],
      });
    } else {
      setForm(DEFAULT_FORM);
    }
    setErrors({});
  }, [isOpen, initialData]);

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setSubmitting(true);
    setErrors({});
    try {
      const payload = {
        title: form.title,
        content: form.content,
        visibility: form.visibility,
        team_id: isTeam && form.team_id ? Number(form.team_id) : null,
        tags: form.tags,
      };
      await onSubmit(payload);
      onClose();
    } catch (err) {
      if (err.status === 422) {
        setErrors(err.errors);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !submitting) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className="project-composer-overlay" onClick={handleBackdropClick}>
      <div className="project-composer-modal modal" role="dialog" aria-modal="true">
        <div className="project-composer-header">
          <h2 className="project-composer-title">
            {initialData ? t('pages.file_manager.notes.form.edit_title') : t('pages.file_manager.notes.form.create_title')}
          </h2>
          <button
            className="project-composer-close"
            onClick={onClose}
            disabled={submitting}
            aria-label={t('common.actions.close')}
          >
            <FaTimes />
          </button>
        </div>

        <div className="project-composer-body">
          <form onSubmit={handleSubmit} id="note-form">
            <div className="project-form-group">
              <label className="project-form-label">
                {t('pages.file_manager.notes.form.title_label')} <span className="required">*</span>
              </label>
              <input
                type="text"
                value={form.title}
                onChange={(e) => setForm((s) => ({ ...s, title: e.target.value }))}
                required
                name="title"
                placeholder={t('pages.file_manager.notes.form.title_placeholder')}
                className={`project-form-input ${errors.title ? 'error' : ''}`}
              />
              {errors.title && <div className="project-form-error">{errors.title[0]}</div>}
            </div>

            <div className="project-form-group">
              <label className="project-form-label">
                {t('pages.file_manager.notes.form.content_label')} <span className="required">*</span>
              </label>
              <textarea
                rows="8"
                value={form.content}
                onChange={(e) => setForm((s) => ({ ...s, content: e.target.value }))}
                required
                name="content"
                placeholder={t('pages.file_manager.notes.form.content_placeholder')}
                className={`project-form-textarea ${errors.content ? 'error' : ''}`}
              />
              {errors.content && <div className="project-form-error">{errors.content[0]}</div>}
            </div>

            <div className="project-form-row">
              <div className="project-form-group">
                <label className="project-form-label">{t('pages.file_manager.notes.form.visibility')}</label>
                <select
                  value={form.visibility}
                  onChange={(e) => setForm((s) => ({ ...s, visibility: e.target.value }))}
                  className="project-form-select"
                >
                  <option value="personal">{t('pages.file_manager.notes.personal')}</option>
                  <option value="team">{t('pages.file_manager.notes.team')}</option>
                </select>
              </div>

              {isTeam && (
                <div className="project-form-group">
                  <label className="project-form-label">{t('pages.file_manager.notes.form.team')}</label>
                  <select
                    value={form.team_id}
                    onChange={(e) => setForm((s) => ({ ...s, team_id: e.target.value }))}
                    required={isTeam}
                    className={`project-form-select ${errors.team_id ? 'error' : ''}`}
                  >
                    <option value="">{t('pages.file_manager.notes.form.select_team')}</option>
                    {teams.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                  {errors.team_id && <div className="project-form-error">{errors.team_id[0]}</div>}
                </div>
              )}
            </div>

            <div className="project-form-group">
              <label className="project-form-label">{t('pages.file_manager.notes.form.tags')}</label>
              <TagInput
                tags={form.tags}
                onChange={(newTags) => setForm((s) => ({ ...s, tags: newTags }))}
                placeholder={t('pages.file_manager.notes.form.tags_placeholder')}
              />
            </div>
          </form>
        </div>

        <div className="project-composer-footer">
          <button
            type="button"
            className="project-composer-btn project-composer-btn-secondary"
            onClick={onClose}
            disabled={submitting}
          >
            {t('common.actions.cancel')}
          </button>
          <button
            type="button"
            className="project-composer-btn project-composer-btn-primary"
            onClick={() => handleSubmit()}
            disabled={submitting}
          >
            {submitting ? t('common.status.saving') : t('common.actions.save_changes')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default NoteFormModal;
